/**
 * Criterion-grouped protocol atom table for draft authoring.
 */

import type {
  ProtocolAtom,
  ProtocolCriterion,
} from "../../api/clinicalTrialsApiTypes";
import { ProtocolTierFilter } from "./ProtocolTierFilter";

interface ProtocolTableProps {
  criteria: ProtocolCriterion[];
  atoms: ProtocolAtom[];
  filterText: string;
  selectedTiers: string[];
  onSelectedTiersChange: (tiers: string[]) => void;
  focusCriterionId?: string | null;
}

/**
 * Render a searchable criterion-grouped atom table.
 *
 * @param props - Table data, shared tier filters, and optional focus criterion
 * @returns Table element
 */
export function ProtocolTable({
  criteria,
  atoms,
  filterText,
  selectedTiers,
  onSelectedTiersChange,
  focusCriterionId = null,
}: ProtocolTableProps) {
  const query = filterText.trim().toLowerCase();
  const orderedCriteria = focusCriterionId
    ? [
        ...criteria.filter(
          (criterion) => criterion.criterion_id === focusCriterionId,
        ),
        ...criteria.filter(
          (criterion) => criterion.criterion_id !== focusCriterionId,
        ),
      ]
    : criteria;
  const filteredCriteria = orderedCriteria.filter((criterion) => {
    const childAtoms = atoms.filter(
      (atom) =>
        atom.criterion_id === criterion.criterion_id &&
        selectedTiers.includes(atom.tier),
    );
    if (childAtoms.length === 0) return false;
    if (!query) return true;
    return (
      criterion.criterion_no.toLowerCase().includes(query) ||
      criterion.criterion_text_verbatim.toLowerCase().includes(query) ||
      childAtoms.some(
        (atom) =>
          atom.atom_id.toLowerCase().includes(query) ||
          atom.atom_text.toLowerCase().includes(query),
      )
    );
  });
  const visibleAtomCount = filteredCriteria.reduce(
    (count, criterion) =>
      count +
      atoms.filter(
        (atom) =>
          atom.criterion_id === criterion.criterion_id &&
          selectedTiers.includes(atom.tier),
      ).length,
    0,
  );

  return (
    <div className="overflow-auto rounded-lg border border-slate-200 bg-white">
      <ProtocolTierFilter
        selectedTiers={selectedTiers}
        visibleAtomCount={visibleAtomCount}
        totalAtomCount={atoms.length}
        visibleCriterionCount={filteredCriteria.length}
        onChange={onSelectedTiersChange}
      />
      <table className="min-w-full text-left text-sm">
        <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Criterion</th>
            <th className="px-3 py-2">Atom</th>
            <th className="px-3 py-2">Tier</th>
            <th className="px-3 py-2">Hard stop</th>
            <th className="px-3 py-2">Logic</th>
            <th className="px-3 py-2">Applies if</th>
            <th className="px-3 py-2">Action</th>
            <th className="px-3 py-2">Review</th>
          </tr>
        </thead>
        <tbody>
          {filteredCriteria.map((criterion) => {
            const childAtoms = atoms.filter(
              (atom) =>
                atom.criterion_id === criterion.criterion_id &&
                selectedTiers.includes(atom.tier),
            );
            const focused = criterion.criterion_id === focusCriterionId;
            return childAtoms.map((atom, index) => (
              <tr
                key={atom.atom_id}
                className={`border-t border-slate-100 align-top ${
                  focused ? "bg-sky-50" : ""
                }`}
              >
                <td className="px-3 py-2">
                  {index === 0 ? (
                    <div>
                      <div className="font-medium text-slate-900">
                        {criterion.criterion_no} · {criterion.criterion_type}
                      </div>
                      <div className="mt-1 max-w-md text-xs text-slate-600">
                        {criterion.criterion_text_verbatim}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        order {criterion.eval_order}
                        {criterion.governing_tier
                          ? ` · ${criterion.governing_tier}`
                          : ""}
                        {criterion.has_hard_stop ? " · hard-stop" : ""}
                      </div>
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <div className="font-mono text-xs text-slate-800">
                    {atom.atom_id}
                  </div>
                  <div className="mt-1 max-w-md text-xs text-slate-600">
                    {atom.atom_text}
                  </div>
                </td>
                <td className="px-3 py-2">{atom.tier}</td>
                <td className="px-3 py-2">
                  {atom.is_hard_stop ? "Yes" : "No"}
                </td>
                <td className="px-3 py-2 text-xs">
                  {atom.logic_group || "—"}
                  {atom.logic_relation ? ` (${atom.logic_relation})` : ""}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {atom.applies_if || "—"}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {atom.crc_action_type || "—"}
                </td>
                <td className="px-3 py-2">
                  {atom.needs_review ? (
                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                      Needs review
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ));
          })}
          {filteredCriteria.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                className="px-3 py-8 text-center text-sm text-slate-500"
              >
                No criteria contain atoms matching the selected tiers and text
                filter.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
